import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Upload, Camera, X, Image, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { compressImages } from "@/lib/imageCompression";

interface PhotoFile {
  file: File;
  preview: string;
  id: string;
}

interface PhotoUploadProps {
  photos: PhotoFile[];
  onPhotosChange: (photos: PhotoFile[]) => void;
  maxPhotos?: number;
}

const PhotoUpload = ({ photos, onPhotosChange, maxPhotos = 10 }: PhotoUploadProps) => {
  const { toast } = useToast();
  const photoInputRef = useRef<HTMLInputElement>(null);
  const [isCompressing, setIsCompressing] = useState(false);

  const isValidImageFile = (file: File): boolean => {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    return validTypes.includes(file.type) && file.size <= 20 * 1024 * 1024; // 20MB max
  };

  const createPhotoFile = (file: File): PhotoFile => ({
    file,
    preview: URL.createObjectURL(file),
    id: crypto.randomUUID(),
  });

  const handlePhotoSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    addPhotos(files);
  };

  const handlePhotoDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const files = Array.from(event.dataTransfer.files);
    addPhotos(files);
  };

  const addPhotos = async (files: File[]) => {
    const validFiles = files.filter(isValidImageFile);
    
    if (validFiles.length !== files.length) {
      toast({
        title: "Some files were skipped",
        description: "Only JPEG, PNG, and WebP images up to 20MB are supported.",
        variant: "destructive",
      });
    }

    const remainingSlots = maxPhotos - photos.length;
    if (validFiles.length > remainingSlots) {
      toast({
        title: "Too many photos",
        description: `You can only upload ${remainingSlots} more photo(s).`,
        variant: "destructive",
      });
      validFiles.splice(remainingSlots);
    }

    if (validFiles.length === 0) return;

    // Compress images before adding
    setIsCompressing(true);
    try {
      const compressedFiles = await compressImages(validFiles, {
        maxWidth: 2048,
        maxHeight: 2048,
        quality: 0.8,
        preserveExif: true,
      });
      
      const newPhotoFiles = compressedFiles.map(createPhotoFile);
      onPhotosChange([...photos, ...newPhotoFiles]);
    } catch (error) {
      console.error('Compression failed:', error);
      // Fall back to original files
      const newPhotoFiles = validFiles.map(createPhotoFile);
      onPhotosChange([...photos, ...newPhotoFiles]);
    } finally {
      setIsCompressing(false);
    }
  };

  const removePhoto = (photoId: string) => {
    const photoToRemove = photos.find(p => p.id === photoId);
    if (photoToRemove) {
      URL.revokeObjectURL(photoToRemove.preview);
      onPhotosChange(photos.filter(p => p.id !== photoId));
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Camera className="h-5 w-5" />
          Photos (Optional)
        </CardTitle>
        <CardDescription>
          Add photos from your adventure - we'll automatically detect obstacles, scenic views, and points of interest to create waypoints
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Photo Upload Drop Zone */}
        <div 
          className="border-2 border-dashed border-border rounded-lg p-8 text-center cursor-pointer hover:border-primary/50 transition-colors"
          onDrop={handlePhotoDrop}
          onDragOver={handleDragOver}
          onClick={() => photoInputRef.current?.click()}
        >
          <input
            ref={photoInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp"
            multiple
            onChange={handlePhotoSelect}
            className="hidden"
          />
          
          <div className="space-y-3">
            {isCompressing ? (
              <>
                <Loader2 className="h-10 w-10 text-primary mx-auto animate-spin" />
                <p className="text-base font-medium text-foreground">
                  Compressing photos...
                </p>
              </>
            ) : (
              <>
                <Image className="h-10 w-10 text-muted-foreground mx-auto" />
                <div>
                  <p className="text-base font-medium text-foreground mb-1">
                    Drag & drop photos here
                  </p>
                  <p className="text-muted-foreground mb-3">or</p>
                  <Button variant="outline" type="button">
                    <Upload className="h-4 w-4 mr-2" />
                    Choose Photos
                  </Button>
                </div>
                <p className="text-sm text-muted-foreground">
                  {photos.length}/{maxPhotos} photos • Auto-compressed for fast uploads
                </p>
              </>
            )}
          </div>
        </div>

        {/* Photo Previews */}
        {photos.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {photos.map((photo) => (
              <div key={photo.id} className="relative group">
                <div className="aspect-square rounded-lg overflow-hidden bg-muted">
                  <img
                    src={photo.preview}
                    alt="Photo preview"
                    className="w-full h-full object-cover photo-signature"
                  />
                </div>
                <Button
                  variant="destructive"
                  size="sm"
                  className="absolute top-2 right-2 h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={(e) => {
                    e.stopPropagation();
                    removePhoto(photo.id);
                  }}
                >
                  <X className="h-3 w-3" />
                </Button>
                <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs p-1 truncate">
                  {photo.file.name}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PhotoUpload;